import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import AdminNavbar from "./components/AdminNavbar";
import AdminLogin from "./components/AdminLogin";
import EventManager from "./components/EventManager";
import EmailManager from "./components/EmailManager";
import BannerManager from "./components/BannerManager";
import ActivityLogManager from "./components/ActivityLogManager";
import MemberEditModal from "./components/MemberEditModal";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";
import { linkWaitlistByEmail } from "@/lib/waitlist";
import { isInvalidWhatsapp } from "@/lib/phone";
import UpcomingEventManager from "./components/UpcomingEventManager";
import WaitingStateManager from "./components/WaitingStateManager";
import WaitlistManager from "./components/WaitlistManager";
import SendAccessLinkModal from "./components/SendAccessLinkModal";
import MemberMergeTool, { MemberMergeToolHandle } from "./components/MemberMergeTool";
import TestimonialManager from "./components/TestimonialManager";
import { detectDuplicateGroups } from "@/lib/memberMerge";

interface Member {
  id: string;
  full_name: string;
  city_state: string;
  profession: string;
  description: string;
  whatsapp: string;
  country_code: string;
  whatsapp_e164: string | null;
  email: string;
  instagram: string;
  website: string;
  photo: string | null;
  photo_position: string;
  status: string;
  merged_into_id: string | number | null;
  merged_links: Record<string, unknown> | null;
  approval_token: string | null;
  created_at: string;
  bio_long: string;
  skills: string;
  languages: string;
  education: string;
  services: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  experience_years: string;
  has_physical_business: boolean;
  business_name: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_zip: string;
  last_access_link_sent_at: string | null;
  last_access_link_type: string | null;
}

type FilterTab = "all" | "pending" | "approved" | "rejected" | "active";
type SortOrder = "recent" | "oldest" | "name-asc" | "name-desc";
type AdminTab = "cadastros" | "eventos" | "emails" | "banners" | "atividades" | "depoimentos" | "proximos-eventos";

interface Toast {
  id: number;
  type: "success" | "error" | "info";
  message: string;
}

type DetailRow = { label: string; value: string | null; icon: string };

function buildDetailRows(m: Member): DetailRow[] {
  const rows: DetailRow[] = [
    { label: "Nome completo", value: m.full_name, icon: "ri-user-line" },
    { label: "Email", value: m.email, icon: "ri-mail-line" },
    { label: "WhatsApp", value: `${m.country_code} ${m.whatsapp}`, icon: "ri-whatsapp-line" },
    { label: "Cidade / Estado", value: m.city_state, icon: "ri-map-pin-line" },
    { label: "Profissão", value: m.profession, icon: "ri-briefcase-line" },
    { label: "Descrição", value: m.description, icon: "ri-file-text-line" },
    { label: "Instagram", value: m.instagram, icon: "ri-instagram-line" },
    { label: "Site", value: m.website, icon: "ri-global-line" },
    { label: "LinkedIn", value: m.linkedin, icon: "ri-linkedin-line" },
    { label: "Facebook", value: m.facebook, icon: "ri-facebook-line" },
    { label: "TikTok", value: m.tiktok, icon: "ri-tiktok-line" },
    { label: "Bio completa", value: m.bio_long, icon: "ri-quill-pen-line" },
    { label: "Habilidades", value: m.skills, icon: "ri-star-line" },
    { label: "Idiomas", value: m.languages, icon: "ri-global-line" },
    { label: "Formação", value: m.education, icon: "ri-graduation-cap-line" },
    { label: "Serviços", value: m.services, icon: "ri-service-line" },
    { label: "Experiência", value: m.experience_years, icon: "ri-timer-line" },
    { label: "Status", value: m.status, icon: "ri-flag-line" },
    { label: "Cadastrada em", value: m.created_at ? new Date(m.created_at).toLocaleString("pt-BR") : null, icon: "ri-calendar-line" },
  ];

  if (m.has_physical_business) {
    rows.push(
      { label: "Nome do negócio", value: m.business_name, icon: "ri-store-2-line" },
      { label: "Endereço do negócio", value: [m.business_address, m.business_city, m.business_state, m.business_zip].filter(Boolean).join(", "), icon: "ri-map-pin-line" },
    );
  }

  return rows;
}

function buildApprovalLink(token: string): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/completar-cadastro?token=${token}`;
}

function buildCommunityLink(): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/comunidade`;
}

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportMembersCsv(rows: Member[]): void {
  const headers = ["nome", "email", "whatsapp", "codigo_pais", "whatsapp_e164", "cidade", "profissao", "status", "data_entrada", "tem_negocio_fisico", "negocio", "endereco", "cidade_negocio", "estado_negocio", "zip_negocio"];
  const lines = [headers.join(",")];
  rows.forEach((m) => {
    const cells = [
      m.full_name || "",
      m.email || "",
      m.whatsapp || "",
      m.country_code || "",
      m.whatsapp_e164 || "",
      m.city_state || "",
      m.profession || "",
      m.status || "",
      m.created_at ? new Date(m.created_at).toLocaleDateString("pt-BR") : "",
      m.has_physical_business ? "Sim" : "Não",
      m.business_name || "",
      m.business_address || "",
      m.business_city || "",
      m.business_state || "",
      m.business_zip || "",
    ].map((v) => csvEscape(v));
    lines.push(cells.join(","));
  });
  const csv = "\uFEFF" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `membros-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const { authState, loginError, login, logout } = useAdminAuth();

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("recent");
  const [activeTab, setActiveTab] = useState<AdminTab>("cadastros");
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [accessLinkMember, setAccessLinkMember] = useState<Member | null>(null);
  const mergeToolRef = useRef<MemberMergeToolHandle>(null);

  let toastCounter = 0;
  function showToast(type: "success" | "error" | "info", message: string) {
    const id = Date.now() + toastCounter++;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 6000);
  }

  useEffect(() => {
    if (authState === "authenticated") {
      loadMembers();
    }
  }, [authState]);

  async function loadMembers() {
    setLoading(true);
    const { data, error } = await supabase
      .from("members")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error && data) {
      setMembers(data as Member[]);
    }
    setLoading(false);
  }

  function generateToken(): string {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    let token = "";
    for (let i = 0; i < 24; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  }

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function sendApprovalEmail(member: Member, token: string): Promise<boolean> {
    try {
      const communityLink = buildCommunityLink();

      // Check if this member has a linked event registration
      let eventPayload: Record<string, unknown> = {};

      let { data: registration } = await supabase
        .from("event_registrations")
        .select("event_id")
        .eq("member_id", member.id)
        .maybeSingle();

      if (!registration) {
        const { data: regByEmail } = await supabase
          .from("event_registrations")
          .select("event_id")
          .ilike("email", member.email)
          .maybeSingle();
        registration = regByEmail;
      }

      if (registration) {
        const { data: event } = await supabase
          .from("upcoming_events")
          .select("title, event_datetime, location")
          .eq("id", registration.event_id)
          .maybeSingle();

        if (event) {
          const eventDate = `${new Date(event.event_datetime).toLocaleDateString("pt-BR", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "America/New_York",
          })} às ${new Date(event.event_datetime).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: "America/New_York",
          })}`;
          eventPayload = {
            eventType: "event",
            eventTitle: event.title,
            eventDate,
            eventLocation: event.location || "",
          };
        }
      }

      const { data: result, error: fnError } = await supabase.functions.invoke("send-approval-email", {
        body: {
          email: member.email,
          name: member.full_name,
          token,
          baseUrl: window.location.origin,
          communityLink,
          ...eventPayload,
        },
      });

      if (fnError) {
        console.error("[Email] Erro na edge function:", fnError);
        showToast("error", `Falha ao enviar email para ${member.full_name}: ${fnError.message || "Erro desconhecido"}`);
        return false;
      }

      if (result && !result.success) {
        console.error("[Email] Resend rejeitou:", result);
        const resendMsg = result.resendError?.message || result.error || "Erro desconhecido";
        showToast("error", `Email para ${member.full_name} falhou: ${resendMsg}`);
        return false;
      }

      showToast("success", `Email enviado com sucesso para ${member.full_name}!`);
      return true;
    } catch (err: any) {
      console.error("[Email] Exceção:", err);
      showToast("error", `Falha ao enviar email para ${member.full_name}: ${err?.message || "Erro inesperado"}`);
      return false;
    }
  }

  async function approve(id: string) {
    const token = generateToken();
    const { error } = await supabase
      .from("members")
      .update({ status: "active", approval_token: token })
      .eq("id", id);

    if (!error) {
      setMembers((prev) =>
        prev.map((m) => (m.id === id ? { ...m, status: "active", approval_token: token } : m)),
      );

      const member = members.find((m) => m.id === id);
      if (member?.email) {
        await sendApprovalEmail(member, token);
        // Vincula a lista de espera pelo e-mail (evita duplicar quando vira membro)
        await linkWaitlistByEmail(member.id, member.email);
      }
    } else {
      showToast("error", "Falha ao atualizar status no banco de dados.");
    }
  }

  async function resendEmail(id: string) {
    const member = members.find((m) => m.id === id);
    if (!member || !member.approval_token) {
      showToast("error", "Esta integrante não possui token de aprovação. Rejeite e aprove novamente.");
      return;
    }
    await sendApprovalEmail(member, member.approval_token);
  }

  async function reject(id: string) {
    const { error } = await supabase
      .from("members")
      .update({ status: "rejected" })
      .eq("id", id);
    if (!error) {
      setMembers((prev) =>
        prev.map((m) => (m.id === id ? { ...m, status: "rejected" } : m)),
      );
    }
  }

  async function deleteMember(id: string) {
    if (duplicateMemberIds.has(id)) {
      const opened = mergeToolRef.current?.openForMember(id);
      showToast(
        "info",
        opened
          ? "Este cadastro tem uma duplicata. A ferramenta UNIFICAR foi aberta — junte os cadastros em vez de excluir."
          : "Este cadastro tem uma duplicata detectada. Resolva pela ferramenta UNIFICAR antes de excluir.",
      );
      return;
    }
    if (!confirm("Tem certeza que deseja excluir este cadastro permanentemente?"))
      return;
    const { error } = await supabase.from("members").delete().eq("id", id);
    if (!error) {
      setMembers((prev) => prev.filter((m) => m.id !== id));
      showToast("success", "Cadastro excluído.");
    }
  }

  async function handleAccessLinkSent(id: string, type: "magiclink" | "recovery") {
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("members")
      .update({ last_access_link_sent_at: now, last_access_link_type: type })
      .eq("id", id);

    if (!error) {
      setMembers((prev) =>
        prev.map((m) => (m.id === id ? { ...m, last_access_link_sent_at: now, last_access_link_type: type } : m)),
      );
    }
  }

  const liveMembers = members.filter(
    (m) => m.status !== "merged" && m.merged_into_id == null,
  );
  const pending = liveMembers.filter((m) => m.status === "pending");
  const approved = liveMembers.filter((m) => m.status === "approved");
  const rejected = liveMembers.filter((m) => m.status === "rejected");
  const active = liveMembers.filter((m) => m.status === "active");

  // Ids que fazem parte de algum grupo de duplicados (usado pra bloquear exclusão).
  const duplicateMemberIds = useMemo(() => {
    const set = new Set<string>();
    detectDuplicateGroups(members).forEach((g) => g.forEach((m) => set.add(String(m.id))));
    return set;
  }, [members]);

  const normalizedSearch = normalize(searchTerm.trim());

  const filteredMembers = liveMembers
    .filter((m) => activeFilter === "all" || m.status === activeFilter)
    .filter((m) => {
      if (!normalizedSearch) return true;
      const haystack = normalize(
        [m.full_name, m.email, m.whatsapp, m.profession].filter(Boolean).join(" "),
      );
      return haystack.includes(normalizedSearch);
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case "name-asc":
          return a.full_name.localeCompare(b.full_name, "pt-BR");
        case "name-desc":
          return b.full_name.localeCompare(a.full_name, "pt-BR");
        case "oldest":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "recent":
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  const filterTabs: { key: FilterTab; label: string; count: number; icon: string; colorClass: string }[] = [
    { key: "all", label: "Todas", count: liveMembers.length, icon: "ri-group-line", colorClass: "text-brown" },
    { key: "pending", label: "Pendentes", count: pending.length, icon: "ri-time-line", colorClass: "text-yellow-600" },
    { key: "approved", label: "Aprovadas", count: approved.length, icon: "ri-checkbox-circle-line", colorClass: "text-green-600" },
    { key: "active", label: "Ativas", count: active.length, icon: "ri-shield-user-line", colorClass: "text-primary" },
    { key: "rejected", label: "Rejeitadas", count: rejected.length, icon: "ri-close-circle-line", colorClass: "text-red-500" },
  ];

  if (authState === "loading" || authState === "checking-admin") {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  if (authState === "login") {
    return <AdminLogin onLogin={login} error={loginError} />;
  }

  if (authState === "not-admin") {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 md:p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-6">
              <i className="ri-lock-2-line text-2xl text-amber-500" />
            </div>
            <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
              Acesso não autorizado
            </h2>
            <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
              Esta conta não tem acesso à área administrativa. Se você é uma
              integrante, seu acesso ao site continua normal — este espaço é
              exclusivo da administração.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={logout}
                className="w-full font-body text-sm px-6 py-3 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
              >
                Sair
              </button>
              <Link
                to="/"
                className="w-full font-body text-sm px-6 py-3 rounded-full bg-white border border-nude text-brown tracking-wide uppercase transition-all duration-300 hover:bg-beige whitespace-nowrap cursor-pointer"
              >
                Voltar ao site
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-beige">
      <AdminNavbar onLogout={logout} />

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="fixed top-20 right-6 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`px-4 py-3 rounded-xl shadow-lg font-body text-sm flex items-start gap-2.5 animate-slide-in ${
                t.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : t.type === "info"
                    ? "bg-amber-50 text-amber-800 border border-amber-200"
                    : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              <i className={`${
                t.type === "success"
                  ? "ri-checkbox-circle-line text-green-500"
                  : t.type === "info"
                    ? "ri-information-line text-amber-500"
                    : "ri-error-warning-line text-red-500"
              } mt-0.5`} />
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="text-brown/30 hover:text-brown/60 cursor-pointer shrink-0"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Área Administrativa
            </p>
            <h2 className="font-display text-2xl md:text-3xl text-brown mb-8">
              {activeTab === "cadastros" ? "Gerenciar Cadastros" : activeTab === "eventos" ? "Gerenciar Eventos" : activeTab === "emails" ? "Email Marketing" : activeTab === "banners" ? "Gerenciar Banners" : activeTab === "depoimentos" ? "Gerenciar Depoimentos" : activeTab === "proximos-eventos" ? "Próximos Eventos" : "Atividades do Site"}
            </h2>

            {/* Tab switcher */}
            <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
              <button
                onClick={() => setActiveTab("cadastros")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "cadastros"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-user-settings-line" />
                Cadastros
              </button>
              <button
                onClick={() => setActiveTab("eventos")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "eventos"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-calendar-event-line" />
                Eventos
              </button>
              <button
                onClick={() => setActiveTab("emails")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "emails"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-mail-send-line" />
                Email Marketing
              </button>
              <button
                onClick={() => setActiveTab("banners")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "banners"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-image-line" />
                Banners
              </button>
              <button
                onClick={() => setActiveTab("atividades")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "atividades"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-bar-chart-line" />
                Atividades
              </button>
              <button
                onClick={() => setActiveTab("proximos-eventos")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "proximos-eventos"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-calendar-todo-line" />
                Próximos Eventos
              </button>
              <button
                onClick={() => setActiveTab("depoimentos")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === "depoimentos"
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-brown hover:bg-beige border border-nude/60"
                }`}
              >
                <i className="ri-chat-quote-line" />
                Depoimentos
              </button>
            </div>
          </div>

          {/* Cadastros tab */}
          {activeTab === "cadastros" && (
            <>
              {/* Ferramenta de mesclagem de cadastros duplicados */}
              <MemberMergeTool ref={mergeToolRef} members={members} onToast={showToast} onRefresh={loadMembers} />

              {/* Search, sort, counter and export toolbar */}
              <div className="bg-white rounded-2xl p-4 md:p-5 mb-6">
                <div className="flex flex-col lg:flex-row gap-3 md:gap-4">
                  <div className="flex-1 relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-search-line text-brown/40" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar por nome, e-mail, WhatsApp ou profissão..."
                      className="w-full pl-11 pr-10 py-3 rounded-xl border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-brown/10 transition-colors cursor-pointer"
                        title="Limpar busca"
                      >
                        <i className="ri-close-line" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2 md:gap-3">
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                      className="px-4 py-3 rounded-xl border border-nude bg-white font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors cursor-pointer"
                    >
                      <option value="recent">Mais recentes</option>
                      <option value="oldest">Mais antigas</option>
                      <option value="name-asc">Nome A-Z</option>
                      <option value="name-desc">Nome Z-A</option>
                    </select>
                    <button
                      onClick={() => exportMembersCsv(filteredMembers)}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                      title="Exportar a lista filtrada e ordenada em CSV"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-download-line" />
                      </div>
                      <span>Exportar CSV</span>
                    </button>
                  </div>
                </div>

                <p className="font-body text-sm text-brown/60 mt-3 md:mt-4">
                  Exibindo <strong className="text-primary">{filteredMembers.length}</strong> de{" "}
                  <strong className="text-primary">{liveMembers.length}</strong> membros
                </p>
              </div>

              {/* Clickable filter tabs */}
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-10">
                {filterTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveFilter(tab.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeFilter === tab.key
                        ? "bg-primary text-white shadow-md"
                        : "bg-white text-brown hover:bg-beige border border-nude/60"
                    }`}
                  >
                    <i className={`${tab.icon} ${activeFilter === tab.key ? "text-white" : tab.colorClass}`} />
                    <span>{tab.label}</span>
                    <span className={`ml-1 font-display text-base ${
                      activeFilter === tab.key ? "text-white/80" : "text-brown/40"
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="text-center py-20">
                  <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                  <p className="font-body text-sm text-brown/60">Carregando cadastros...</p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center">
                  <i className="ri-inbox-line text-4xl text-brown/20 mb-4" />
                  <p className="font-body text-brown/60">Nenhum cadastro encontrado para este filtro.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMembers.map((m) => {
                    const isExpanded = expandedIds.has(m.id);
                    const detailRows = buildDetailRows(m);
                    const isPending = m.status === "pending";
                    const isApproved = m.status === "approved";
                    const isActive = m.status === "active";
                    const isRejected = m.status === "rejected";
                    const missingPhoto = !m.photo;
                    const missingBio = !(m.bio_long || m.description);

                    return (
                      <div key={m.id} className={`bg-white rounded-2xl p-5 md:p-6 ${isRejected ? "opacity-70" : ""}`}>
                        <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                          {/* Photo */}
                          {m.photo ? (
                            <img
                              src={m.photo}
                              alt={m.full_name}
                              className={`rounded-xl object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(m.photo_position)]} shrink-0 ${isPending ? "w-20 h-20" : "w-16 h-16"}`}
                            />
                          ) : (
                            <MemberPhotoPlaceholder
                              size={isPending ? "lg" : "md"}
                              rounded="xl"
                            />
                          )}

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h4 className={`font-display text-brown ${isPending ? "text-lg" : "text-base"}`}>{m.full_name}</h4>
                              {isPending && (
                                <span className="px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-body text-xs uppercase tracking-wide">Pendente</span>
                              )}
                              {isApproved && (
                                <span className="px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 font-body text-xs uppercase tracking-wide">Aprovada</span>
                              )}
                              {isActive && (
                                <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-body text-xs uppercase tracking-wide">Ativa</span>
                              )}
                              {isRejected && (
                                <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-body text-xs uppercase tracking-wide">Rejeitada</span>
                              )}
                              {missingPhoto && (
                                <span className="px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-body text-xs" title="Este cadastro ainda não tem foto">
                                  Sem foto
                                </span>
                              )}
                              {missingBio && (
                                <span className="px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-body text-xs" title="Este cadastro ainda não tem bio">
                                  Sem bio
                                </span>
                              )}
                              {isInvalidWhatsapp(m.country_code, m.whatsapp) && (
                                <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-body text-xs" title="Telefone incompleto ou inválido para o país selecionado — confirme com a integrante">
                                  <i className="ri-error-warning-line" /> Telefone a confirmar
                                </span>
                              )}
                            </div>
                            <p className="font-body text-sm text-primary mb-1">{m.profession}</p>
                            <p className="font-body text-sm text-brown/60 mb-2">{m.city_state}</p>

                            {/* Pending: show more info */}
                            {isPending && (
                              <>
                                <p className="font-body text-sm text-brown/70 leading-relaxed mb-3">{m.description}</p>
                                <div className="flex flex-wrap items-center gap-3 text-xs font-body text-brown/50">
                                  <span className="flex items-center gap-1"><i className="ri-whatsapp-line" /> {m.country_code} {m.whatsapp}</span>
                                  <span className="flex items-center gap-1"><i className="ri-mail-line" /> {m.email}</span>
                                  {m.instagram && <span className="flex items-center gap-1"><i className="ri-instagram-line" /> {m.instagram}</span>}
                                  {m.website && <span className="flex items-center gap-1"><i className="ri-global-line" /> {m.website}</span>}
                                </div>
                              </>
                            )}

                            {/* Non-pending: show compact info */}
                            {!isPending && (
                              <p className="font-body text-xs text-brown/50 mt-1">{m.email}</p>
                            )}

                            {/* Approved: show approval link */}
                            {isApproved && m.approval_token && (
                              <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                <input
                                  type="text"
                                  readOnly
                                  value={buildApprovalLink(m.approval_token)}
                                  className="flex-1 w-full px-3 py-2 rounded-lg bg-beige/50 border border-nude font-mono text-xs text-brown/70 focus:outline-none"
                                />
                                <div className="flex gap-2 shrink-0">
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(buildApprovalLink(m.approval_token));
                                      showToast("success", "Link copiado!");
                                    }}
                                    className="px-3 py-2 rounded-lg bg-primary/10 text-primary font-body text-xs hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                                    title="Copiar link"
                                  >
                                    <i className="ri-file-copy-line" /> Copiar
                                  </button>
                                  <button
                                    onClick={() => resendEmail(m.id)}
                                    className="px-3 py-2 rounded-lg bg-accent-100 text-accent-700 font-body text-xs hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                                    title="Reenviar email"
                                  >
                                    <i className="ri-mail-send-line" /> Reenviar
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Toggle "Ver tudo" for all */}
                            <button
                              onClick={() => toggleExpand(m.id)}
                              className="mt-3 flex items-center gap-1.5 font-body text-xs text-primary hover:text-primary-dark transition-colors cursor-pointer"
                            >
                              <i className={isExpanded ? "ri-arrow-up-circle-line" : "ri-arrow-down-circle-line"} />
                              {isExpanded ? "Recolher detalhes" : "Ver todos os detalhes"}
                            </button>
                          </div>

                          {/* Action buttons */}
                          <div className="flex md:flex-col gap-2 shrink-0">
                            {isPending && (
                              <>
                                <button
                                  onClick={() => setEditingMember(m)}
                                  className="px-4 py-2 rounded-lg bg-amber-500 text-white font-body text-sm hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-edit-line mr-1" /> Editar
                                </button>
                                <button
                                  onClick={() => approve(m.id)}
                                  className="px-4 py-2 rounded-lg bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-check-line mr-1" /> Aprovar
                                </button>
                                <button
                                  onClick={() => reject(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-close-line mr-1" /> Rejeitar
                                </button>
                                <button
                                  onClick={() => deleteMember(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line mr-1" /> Excluir
                                </button>
                              </>
                            )}
                            {isApproved && (
                              <>
                                <button
                                  onClick={() => setAccessLinkMember(m)}
                                  className="px-4 py-2 rounded-lg bg-accent-100 text-accent-700 font-body text-sm hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-magic-line mr-1" /> Enviar link de acesso
                                </button>
                                <button
                                  onClick={() => setEditingMember(m)}
                                  className="px-4 py-2 rounded-lg bg-amber-500 text-white font-body text-sm hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-edit-line mr-1" /> Editar
                                </button>
                                <button
                                  onClick={() => reject(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  Rejeitar
                                </button>
                                <button
                                  onClick={() => deleteMember(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line mr-1" /> Excluir
                                </button>
                              </>
                            )}
                            {isActive && (
                              <>
                                <button
                                  onClick={() => setAccessLinkMember(m)}
                                  className="px-4 py-2 rounded-lg bg-accent-100 text-accent-700 font-body text-sm hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-magic-line mr-1" /> Enviar link de acesso
                                </button>
                                <button
                                  onClick={() => setEditingMember(m)}
                                  className="px-4 py-2 rounded-lg bg-amber-500 text-white font-body text-sm hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-edit-line mr-1" /> Editar
                                </button>
                                <button
                                  onClick={() => reject(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  Rejeitar
                                </button>
                                <button
                                  onClick={() => deleteMember(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line mr-1" /> Excluir
                                </button>
                              </>
                            )}
                            {isRejected && (
                              <>
                                <button
                                  onClick={() => setEditingMember(m)}
                                  className="px-4 py-2 rounded-lg bg-amber-500 text-white font-body text-sm hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-edit-line mr-1" /> Editar
                                </button>
                                <button
                                  onClick={() => approve(m.id)}
                                  className="px-4 py-2 rounded-lg bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-check-line mr-1" /> Aprovar
                                </button>
                                <button
                                  onClick={() => deleteMember(m.id)}
                                  className="px-4 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line mr-1" /> Excluir
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Expanded detail panel */}
                        {isExpanded && (
                          <div className="mt-5 pt-5 border-t border-nude">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {detailRows.map((row) => (
                                <div
                                  key={row.label}
                                  className="flex items-start gap-2.5 p-3 rounded-xl bg-beige/60"
                                >
                                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary/10 shrink-0 mt-0.5">
                                    <i className={`${row.icon} text-xs text-primary`} />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-body text-[11px] text-brown/40 uppercase tracking-wide mb-0.5">
                                      {row.label}
                                    </p>
                                    <p className="font-body text-sm text-brown break-words">
                                      {row.value || <span className="text-brown/30 italic">Não informado</span>}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Eventos tab */}
          {activeTab === "eventos" && <EventManager />}

          {/* Email Marketing tab */}
          {activeTab === "emails" && <EmailManager />}

          {/* Banners tab */}
          {activeTab === "banners" && <BannerManager />}

          {/* Atividades tab */}
          {activeTab === "atividades" && <ActivityLogManager />}

          {/* Próximos Eventos tab */}
          {activeTab === "proximos-eventos" && (
  <>
    <WaitingStateManager onToast={showToast} />
    <UpcomingEventManager onToast={showToast} />
    <WaitlistManager onToast={showToast} />
  </>
)}

          {/* Depoimentos tab */}
          {activeTab === "depoimentos" && <TestimonialManager onToast={showToast} />}
        </div>
      </div>

      {/* Send Access Link Modal */}
      {accessLinkMember && (
        <SendAccessLinkModal
          member={accessLinkMember}
          onClose={() => setAccessLinkMember(null)}
          onSent={(type) => handleAccessLinkSent(accessLinkMember.id, type)}
          onToast={showToast}
        />
      )}

      {/* Member Edit Modal */}
      {editingMember && (
        <MemberEditModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSaved={() => {
            setEditingMember(null);
            loadMembers();
          }}
          onToast={showToast}
        />
      )}
    </div>
  );
}